import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, where, Timestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Box, Typography, Skeleton, CssBaseline } from '@mui/material';
import { MdTimer } from 'react-icons/md';
import { ArrowForwardIos, ArrowBack, Star } from '@mui/icons-material';
import NavBar from '../../components/NavBar';
import { onAuthStateChanged } from 'firebase/auth';
import fallbackImage from '../../images/Barber Icon (SVG).svg';
import { colors, typography, radius, chipStyle, glassCard } from '../../theme';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function getTodayHours(barber, shop) {
    const dayKey = DAY_KEYS[new Date().getDay()];
    const hours = barber.operatingHours?.[dayKey] ?? shop?.operatingHours?.[dayKey];
    if (!hours || hours.isOff) return null;
    return hours;
}

function formatHoursLabel(hours) {
    if (!hours) return 'Off today';
    return `${formatTo12(hours.open)} – ${formatTo12(hours.close)}`;
}

function formatTo12(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    return `${h % 12 === 0 ? 12 : h % 12}:${m.toString().padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

const Barbers = () => {
    const { shopId } = useParams();
    const navigate = useNavigate();
    const [barbers, setBarbers] = useState([]);
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async () => {
            try {
                const shopSnap = await getDoc(doc(db, 'shops', shopId));
                const shopData = shopSnap.exists() ? { id: shopSnap.id, ...shopSnap.data() } : null;
                setShop(shopData);

                const today = new Date(); today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
                const snap = await getDocs(collection(db, 'shops', shopId, 'barbers'));

                const data = await Promise.all(snap.docs.map(async (d) => {
                    const barberData = { id: d.id, ...d.data() };
                    try {
                        const q = query(
                            collection(db, 'bookings'),
                            where('barberId', '==', d.id),
                            where('scheduledAt', '>=', Timestamp.fromDate(today)),
                            where('scheduledAt', '<', Timestamp.fromDate(tomorrow)),
                            where('status', '!=', 'cancelled'),
                        );
                        barberData.todayBookings = (await getDocs(q)).size;
                    } catch { barberData.todayBookings = 0; }
                    return barberData;
                }));
                setBarbers(data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        });
        return () => unsub();
    }, [shopId]);

    const filteredBarbers = barbers.filter(b => {
        if (b.isAvailable === false) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return b.name?.toLowerCase().includes(q) || b.specialty?.some(s => s.toLowerCase().includes(q));
    });

    if (loading) return (
        <Box sx={{ bgcolor: colors.bg, minHeight: '100vh' }}>
            <CssBaseline />
            <NavBar />
            <Box sx={{ px: 3, mt: 4 }}>
                <Skeleton variant="text" width="50%" height={40} sx={{ bgcolor: colors.bgCard, mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" width="30%" height={20} sx={{ bgcolor: colors.bgCard, mx: 'auto', mb: 4 }} />
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rounded" height={130} sx={{ bgcolor: colors.bgCard, mb: 2, borderRadius: radius.lg }} />
                ))}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ bgcolor: colors.bgGradient, minHeight: '100vh', color: colors.textPrimary, pb: 8 }}>
            <CssBaseline />
            <NavBar
                showSearch
                searchValue={search}
                onSearch={setSearch}
                placeholder="Search Barber Here..."
                title='Find Your Barber'
            />
            {/* ── Shop Hero Header ── */}
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    bgcolor: colors.primary,
                    borderRadius: 3,
                    p: 2,
                    boxShadow: `0 4px 20px ${colors.primary}66`,
                }}>
                    {/* Shop image thumbnail */}
                    <Box sx={{
                        width: 72, height: 72, flexShrink: 0,
                        borderRadius: 2, overflow: 'hidden',
                        bgcolor: 'rgba(50,41,32,0.15)',
                        border: '2px solid rgba(50,41,32,0.15)',
                    }}>
                        <Box component="img"
                            src={shop?.image || fallbackImage}
                            alt={shop?.shopName}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2, mb: 0.3 }} noWrap>
                            {shop?.shopName ?? 'Loading...'}
                        </Typography>
                        {shop?.address && (
                            <Typography sx={{ fontSize: 11, color:colors.textPrimary, mb: 0.2 }} noWrap>
                                📍 {shop.address}
                            </Typography>
                        )}
                        {shop?.operatingHours && (() => {
                            const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                            const hours = shop.operatingHours[DAY_KEYS[new Date().getDay()]];
                            if (!hours || hours.isOff) return <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>🕐 Closed today</Typography>;
                            return <Typography sx={{ fontSize: 11, color: colors.textPrimary}}>🕐 {formatTo12(hours.open)} – {formatTo12(hours.close)}</Typography>;
                        })()}
                        {shop?.phone && (
                            <Typography sx={{ fontSize: 11, color:colors.textPrimary}}>📞 {shop.phone}</Typography>
                        )}
                    </Box>

                    {/* Walk-in button */}
                    <Box onClick={() => navigate(`/walkin/${shopId}`)} sx={{
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        bgcolor: colors.success,
                        border: `1px solid ${colors.bg}`,
                        borderRadius: 999, px: 1.2, py: 0.5,
                        cursor: 'pointer', transition: 'all .2s',
                        '&:hover': { bgcolor: 'rgba(34,197,94,0.35)' },
                    }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.bg, boxShadow: `0 0 6px ${colors.bg}` }} />
                        <Typography sx={{ fontSize: 10, color: colors.bg, fontWeight: 700 }}>Walk In</Typography>
                    </Box>
                </Box>
            </Box>

            {/* ── Section title ── */}
            <Box sx={{ px: 3, mt: 3, pb:2, backgroundColor:colors.bg }}>
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
                    Choose Your Barber
                </Typography>
                <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 0.3 }}>
                    Tap a barber to view availability and book
                </Typography>
            </Box>

            {/* ── Barber list ── */}
            {filteredBarbers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 , backgroundColor:colors.bg}}>
                    <Typography sx={{ fontSize: 32 }}>✂️</Typography>
                    <Typography sx={{ fontSize: 16, color: colors.textFaint, mt: 1 }}>
                        {search ? 'No barbers match your search' : 'No barbers available right now'}
                    </Typography>
                    {search && (
                        <Typography onClick={() => setSearch('')} sx={{ fontSize: 13, color: colors.primary, mt: 1, cursor: 'pointer' }}>
                            Clear search
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{ px: 3, display: 'flex', flexDirection: 'column', gap: 2 , backgroundColor:colors.bg}}>
                    {filteredBarbers.map((barber) => {
                        const todayHours = getTodayHours(barber, shop);
                        const hoursLabel = formatHoursLabel(todayHours);
                        const isWorkingToday = !!todayHours;
                        const rating = barber.rating ?? barber.ratings?.average ?? 0;
                        const ratingCount = barber.ratingCount ?? barber.ratings?.totalReviews ?? 0;

                        return (
                            <Box
                                key={barber.id}
                                onClick={() => navigate(`/shop/${shopId}/barber/${barber.id}/book`)}
                                sx={{
                                    display: 'flex',
                                    bgcolor: colors.bgCard,
                                    borderRadius: radius.lg,
                                    border: `1px solid ${colors.textPrimary}`,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'border-color .2s, transform .2s',
                                    '&:hover': { borderColor: colors.primaryBorder, transform: 'translateY(-2px)' },
                                    opacity: isWorkingToday ? 1 : 0.6,
                                    
                                }}
                            >
                                {/* ── Photo ── */}
                                <Box sx={{ position: 'relative', flexShrink: 0, width: 110 }}>
                                    <Box component="img" src={barber.image || fallbackImage} alt={barber.name}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block', minHeight: 130 }} />

                                    {/* Availability dot */}
                                    <Box sx={{
                                        position: 'absolute', bottom: 8, left: 8,
                                        width: 8, height: 8, borderRadius: '50%',
                                        bgcolor: isWorkingToday ? colors.success : colors.error,
                                        border: `2px solid ${colors.bgCard}`,
                                    }} />

                                    {/* Off-today badge */}
                                    {!isWorkingToday && (
                                        <Box sx={{
                                            position: 'absolute', top: 8, left: 6,
                                            bgcolor: colors.errorBg, border: `1px solid ${colors.errorBorder}`,
                                            borderRadius: radius.sm, px: 0.8, py: 0.2,
                                        }}>
                                            <Typography sx={{ fontSize: 9, color: colors.error, fontWeight: 600 }}>OFF TODAY</Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* ── Info ── */}
                                <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

                                    {/* Name + rating */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography sx={{ fontFamily: typography.poppins, fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
                                            {barber.name}
                                        </Typography>
                                        {rating > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                <Star sx={{ fontSize: 12, color: colors.warning }} />
                                                <Typography sx={{ fontSize: 12, color: colors.warning, fontWeight: 600 }}>{rating.toFixed(1)}</Typography>
                                                {ratingCount > 0 && (
                                                    <Typography sx={{ fontSize: 10, color: colors.textFaintest }}>({ratingCount})</Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Experience */}
                                    {barber.experience && (
                                        <Typography sx={{ fontSize: 12, color: colors.textPrimary }}>
                                            ✂️ {barber.experience} yrs experience
                                        </Typography>
                                    )}

                                    {/* Hours */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, color: isWorkingToday ? colors.textPrimary : colors.textPrimary }}>
                                            🕒 {hoursLabel}
                                        </Typography>
                                        {barber.operatingHours && (
                                            <Box sx={{ px: 0.6, py: 0.1, bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.sm,  }}>
                                                <Typography sx={{ fontSize: 8, color: colors.textPrimary }}>custom</Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Queue + bookings badges */}
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 0.3,
                                            bgcolor: colors.bgDeep, px: 1, py: 0.3,
                                            borderRadius: radius.sm, border: `1px solid ${colors.textPrimary}`,
                                        }}>
                                            <MdTimer style={{ fontSize: 11, color: colors.textPrimary }} />
                                            <Typography sx={{ fontSize: 11, color: colors.textPrimary }}>
                                                {barber.waitingList?.length ?? 0} in queue
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            px: 1, py: 0.3, borderRadius: radius.sm,
                                            bgcolor: barber.todayBookings === 0 ? colors.successBg : colors.warningBg,
                                            border: `1px solid ${barber.todayBookings === 0 ? colors.successBorder : colors.warningBorder}`,
                                        }}>
                                            <Typography sx={{ fontSize: 11, color: barber.todayBookings === 0 ? colors.success : colors.warning }}>
                                                {barber.todayBookings === 0 ? 'Free today' : `${barber.todayBookings} booked today`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Specialty chips */}
                                    {barber.specialty?.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {barber.specialty.slice(0, 3).map((s, i) => (
                                                <Box key={i} sx={{
                                                    px: 1, py: 0.2, borderRadius: radius.sm,
                                                    bgcolor: colors.primary, border: `1px solid ${colors.border}`,
                                                }}>
                                                    <Typography sx={{ fontSize: 10, color: colors.textPrimary }}>{s.trim()}</Typography>
                                                </Box>
                                            ))}
                                            {barber.specialty.length > 3 && (
                                                <Typography sx={{ fontSize: 10, color: colors.textFaint, alignSelf: 'center' }}>
                                                    +{barber.specialty.length - 3} more
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* ── Chevron ── */}
                                <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
                                    <ArrowForwardIos sx={{ fontSize: 14, color: colors.textFaintest }} />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default Barbers;